import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { TranslateService } from '@ngx-translate/core';
import type { Feature, Polygon as GJPolygon } from 'geojson';
import { MIN_CIRCLE_HA, MAX_CIRCLE_HA } from './map-editor.constants';
import { firstHoleOutsideRing, ringLeavesLongitudeRange } from './map-geometry.utils';
import { MAP_EDITOR_ICONS } from './map-editor-icons';
import { EDITOR_INPUT_STYLE, EDITOR_BUTTON_STYLE, EDITOR_PRIMARY_STYLE, styleEditorContainer } from './map-editor-ui';

export interface CircleEditorInit {
  id?: string;
  lat: number;
  lng: number;
  radiusHa?: number;
}

export interface MapPopupEditorContext {
  map: mapboxgl.Map;
  marker: mapboxgl.Marker;
  draw: MapboxDraw;
  coordsPopup: mapboxgl.Popup | null;
  translate: Pick<TranslateService, 'instant'>;
  showErr: (key: string, error?: unknown) => void;
  showErrWith: (key: string, params: Record<string, unknown>) => void;
  showMsg: (key: string) => void;
  getActivePolygon: () => { id: string; feature: Feature<GJPolygon>; ring: [number, number][] } | null;
  buildCircleFeature: (lng: number, lat: number, radiusHa: number) => Feature<GJPolygon>;
  replacePolygon: (id: string, ring: [number, number][], properties?: Record<string, any>) => string | null;
  addCircleToDraw: (lng: number, lat: number, radiusHa: number, name?: string) => string | null;
  zoomToPolygon: (id: string) => void;
  refreshCtrl: () => void;
  confirmLargePolygon: (count: number) => Promise<boolean>;
  whispDetail?: (feature: Feature) => HTMLElement | null;
}

function appendWhispDetail(context: MapPopupEditorContext, wrap: HTMLElement, feature: Feature | undefined) {
  const detail = feature ? context.whispDetail?.(feature) : null;
  if (detail) wrap.appendChild(detail);
}

export function coordinatesTable(translate: Pick<TranslateService, 'instant'>, actionColumn: boolean) {
  const table = document.createElement('table');
  table.className = 'coords-table';
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';
  table.style.tableLayout = 'fixed';

  const thead = document.createElement('thead');
  thead.innerHTML = `
      <tr>
        <th style="text-align:left; padding:4px 6px;">#</th>
        <th style="text-align:left; padding:4px 6px;">Latitude</th>
        <th style="text-align:left; padding:4px 6px;">Longitude</th>
        ${actionColumn ? '<th style="width:1%;"></th>' : ''}
      </tr>`;
  table.appendChild(thead);
  const headings = thead.querySelectorAll('th');
  headings[0].style.width = '34px';
  headings[1].textContent = translate.instant('Map_point_latitude_tr');
  headings[2].textContent = translate.instant('Map_point_longitude_tr');
  if (actionColumn) headings[3].style.width = '34px';
  headings.forEach((heading) => {
    heading.style.position = 'sticky';
    heading.style.top = '0';
    heading.style.background = '#fff';
    heading.style.zIndex = '1';
  });

  const tbody = document.createElement('tbody');
  table.appendChild(tbody);
  return { table, tbody };
}

export function openCircleEditor(this: MapPopupEditorContext, init: CircleEditorInit) {
  if (this.coordsPopup) {
    this.coordsPopup.remove();
    this.coordsPopup = null;
  }

  let at = new mapboxgl.LngLat(init.lng, init.lat);
  if (init.id) {
    try {
      const f = this.draw.get(init.id) as Feature<GJPolygon> | undefined;
      const center = (turf.centroid(f as any) as any)?.geometry?.coordinates as [number, number];
      if (center?.every(Number.isFinite)) at = new mapboxgl.LngLat(center[0], center[1]);
    } catch {}
  }

  const wrap = document.createElement('div');
  wrap.className = 'coords-popup circle-popup';
  wrap.style.minWidth = '360px';

  const title = document.createElement('div');
  title.className = 'coords-popup__h';
  title.textContent = init.id ? 'Edit circle' : 'Create circle';
  wrap.appendChild(title);

  const nameLabel = document.createElement('label');
  nameLabel.textContent = this.translate.instant('Map_area_name_tr');
  nameLabel.style.display = 'block';
  nameLabel.style.fontSize = '12px';
  nameLabel.style.marginBottom = '4px';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.value = init.id ? String((this.draw.get(init.id) as any)?.properties?.name ?? '').trim() : '';
  nameInput.placeholder = this.translate.instant('Map_area_name_tr');
  nameInput.maxLength = 120;
  nameInput.style.width = '100%';
  nameInput.style.boxSizing = 'border-box';
  nameInput.style.padding = '6px';
  nameInput.style.marginBottom = '8px';
  wrap.appendChild(nameLabel);
  wrap.appendChild(nameInput);
  appendWhispDetail(this, wrap, init.id ? (this.draw.get(init.id) as Feature | undefined) : undefined);

  const form = document.createElement('div');
  form.style.display = 'grid';
  form.style.gridTemplateColumns = '1fr 1fr';
  form.style.gap = '8px';

  const mkInput = (label: string, value: string, step = '0.000001') => {
    const wrap = document.createElement('div');
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    const l = document.createElement('label');
    l.textContent = label;
    l.style.fontSize = '12px';
    const inp = document.createElement('input');
    inp.type = 'number';
    inp.step = step;
    inp.value = value;
    inp.style.padding = '6px';
    wrap.appendChild(l);
    wrap.appendChild(inp);
    return { wrap, input: inp };
  };

  const latEl = mkInput('Latitude', String(init.lat));
  const lngEl = mkInput('Longitude', String(init.lng));
  const radEl = mkInput('Radius (ha)', String(init.radiusHa ?? 4), '0.01');
  radEl.input.min = String(MIN_CIRCLE_HA);
  radEl.input.max = String(MAX_CIRCLE_HA);

  form.appendChild(latEl.wrap);
  form.appendChild(lngEl.wrap);
  const radiusWrap = document.createElement('div');
  radiusWrap.style.gridColumn = '1 / span 2';
  radiusWrap.appendChild(radEl.wrap);
  form.appendChild(radiusWrap);

  const btnRow = document.createElement('div');
  btnRow.style.display = 'flex';
  btnRow.style.gap = '8px';
  btnRow.style.marginTop = '10px';

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = 'Save';
  saveBtn.style.textTransform = 'uppercase';
  saveBtn.className = 'coords-popup__btn';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.textTransform = 'uppercase';
  cancelBtn.className = 'coords-popup__btn';

  btnRow.appendChild(saveBtn);
  btnRow.appendChild(cancelBtn);

  wrap.appendChild(form);
  wrap.appendChild(btnRow);

  cancelBtn.addEventListener('click', () => {
    try {
      this.coordsPopup?.remove();
    } finally {
      this.coordsPopup = null;
    }
  });

  saveBtn.addEventListener('click', () => {
    const lat = parseFloat(latEl.input.value);
    const lng = parseFloat(lngEl.input.value);
    let radiusHa = parseFloat(radEl.input.value);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radiusHa) || radiusHa <= 0) {
      this.showErr('Please_enter_valid_numeric_lat_lng_tr');
      return;
    }
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      this.showErr('Lat_must_be_between_90_and_Lng_between_180_tr');
      return;
    }
    if (radiusHa > MAX_CIRCLE_HA) {
      radiusHa = MAX_CIRCLE_HA;
      this.showMsg('Circle_radius_capped_at_4_ha_tr');
    }
    if (radiusHa < MIN_CIRCLE_HA) {
      radiusHa = MIN_CIRCLE_HA;
      this.showMsg('Circle_radius_raised_to_minimum_ha_tr');
    }

    const circle = this.buildCircleFeature(lng, lat, radiusHa);
    const ring = (circle.geometry.coordinates?.[0] ?? []) as [number, number][];
    if (ringLeavesLongitudeRange(ring)) {
      this.showErr('Map_circle_crosses_antimeridian_tr');
      return;
    }

    if (init.id) {
      const nextId = this.replacePolygon(init.id, ring, {
        _circle: true,
        shape: 'circle',
        centerLng: lng,
        centerLat: lat,
        radiusHa,
        name: uniqueFeatureName(nameInput.value, this.draw.getAll().features, init.id),
      });
      if (nextId) {
        this.showMsg('Circle_updated_tr');
        this.coordsPopup?.remove();
        this.coordsPopup = null;
        this.zoomToPolygon(nextId);
      }
    } else {
      const id = this.addCircleToDraw(
        lng,
        lat,
        radiusHa,
        uniqueFeatureName(nameInput.value, this.draw.getAll().features)
      );
      if (id) {
        this.showMsg('Circle_added_tr');
        this.coordsPopup?.remove();
        this.coordsPopup = null;
        this.zoomToPolygon(id);
      }
    }
  });

  try {
    this.coordsPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12, maxWidth: '420px' })
      .setLngLat(at)
      .setDOMContent(wrap)
      .addTo(this.map);
  } catch (err) {
    this.showErr('Could_not_open_circle_editor_tr', err);
  }
}

export async function openCoordsPopup(this: MapPopupEditorContext) {
  if (this.coordsPopup) {
    this.coordsPopup.remove();
    this.coordsPopup = null;
  }
  const active = this.getActivePolygon();
  if (!active) return;

  const props = (active.feature.properties ?? {}) as any;
  if (props._circle) {
    const rHa = Number.isFinite(props.radiusHa)
      ? Number(props.radiusHa)
      : Math.max(0.01, turf.area(active.feature as any) / 10000);
    const lat = Number.isFinite(props.centerLat) ? Number(props.centerLat) : (active.ring[0]?.[1] ?? 0);
    const lng = Number.isFinite(props.centerLng) ? Number(props.centerLng) : (active.ring[0]?.[0] ?? 0);
    openCircleEditor.call(this, { id: active.id, lat, lng, radiusHa: rHa });
    return;
  }

  const fullRing = active.ring;
  const openRing: [number, number][] = fullRing.length > 1 ? fullRing.slice(0, fullRing.length - 1) : [...fullRing];
  let largePolygonConfirmed = false;
  if (openRing.length > 100) {
    if (!(await this.confirmLargePolygon(openRing.length))) return;
    if (!this.draw.get(active.id)) return;
    largePolygonConfirmed = true;
  }

  let at = this.marker.getLngLat();
  try {
    const center = (turf.centroid(active.feature) as any)?.geometry?.coordinates as [number, number];
    if (center?.every(Number.isFinite)) at = new mapboxgl.LngLat(center[0], center[1]);
  } catch {}

  const wrap = document.createElement('div');
  wrap.className = 'coords-popup coords-popup--table';
  styleEditorContainer(wrap, 'min(420px,75vw)');
  wrap.style.maxHeight = 'min(520px,70vh)';
  wrap.setAttribute('aria-label', this.translate.instant('Map_edit_area_tr'));

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.value = String((active.feature.properties as any)?.name ?? '').trim();
  nameInput.placeholder = this.translate.instant('Map_area_name_tr');
  nameInput.setAttribute('aria-label', nameInput.placeholder);
  nameInput.maxLength = 120;
  nameInput.style.width = '100%';
  nameInput.style.boxSizing = 'border-box';
  nameInput.style.padding = '6px';
  nameInput.style.marginBottom = '8px';
  wrap.appendChild(nameInput);
  appendWhispDetail(this, wrap, active.feature);

  const wrap_t = document.createElement('div');
  wrap_t.className = 'coords-wrap_t';
  nameInput.style.cssText = `${EDITOR_INPUT_STYLE}flex-shrink:0;`;
  wrap_t.style.maxHeight = 'min(280px,35vh)';
  wrap_t.style.minHeight = '0';
  wrap_t.style.flex = '1 1 auto';
  wrap_t.style.overflowY = 'auto';

  const { table, tbody } = coordinatesTable(this.translate, true);

  const btnRow = document.createElement('div');
  btnRow.style.display = 'flex';
  btnRow.style.gap = '8px';
  btnRow.style.marginTop = '8px';
  btnRow.style.flexShrink = '0';
  btnRow.style.flexWrap = 'wrap';

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.textContent = this.translate.instant('Map_add_vertex_tr');
  addBtn.className = 'coords-popup__btn';
  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = this.translate.instant('Save_tr');
  saveBtn.className = 'coords-popup__btn';
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = this.translate.instant('cancel_tr');
  cancelBtn.className = 'coords-popup__btn';

  btnRow.appendChild(addBtn);
  btnRow.appendChild(saveBtn);
  btnRow.appendChild(cancelBtn);
  for (const button of [addBtn, saveBtn, cancelBtn]) button.style.cssText = EDITOR_BUTTON_STYLE;
  saveBtn.style.cssText += EDITOR_PRIMARY_STYLE;

  wrap_t.appendChild(table);
  wrap.appendChild(wrap_t);
  wrap.appendChild(btnRow);

  const makeNumberInput = (val: number, placeholder: string) => {
    const inp = document.createElement('input');
    inp.type = 'number';
    inp.step = '0.000001';
    inp.placeholder = placeholder;
    inp.value = Number.isFinite(val) ? String(val) : '';
    inp.style.cssText = EDITOR_INPUT_STYLE;
    return inp;
  };

  const rebuildRowIndices = () => {
    Array.from(tbody.querySelectorAll('tr')).forEach((tr, idx) => {
      const first = tr.querySelector('.idx') as HTMLSpanElement;
      if (first) first.textContent = String(idx + 1);
    });
  };

  const addRow = (lat = openRing.at(-1)?.[1] ?? 0, lng = openRing.at(-1)?.[0] ?? 0, renumber = true) => {
    const tr = document.createElement('tr');
    tr.style.borderTop = '1px solid #e5e5e5';

    const tdIdx = document.createElement('td');
    tdIdx.style.padding = '4px 6px';
    const idxSpan = document.createElement('span');
    idxSpan.className = 'idx';
    tdIdx.appendChild(idxSpan);

    const tdLat = document.createElement('td');
    tdLat.style.padding = '4px 6px';
    const inpLat = makeNumberInput(lat, 'Lat');
    tdLat.appendChild(inpLat);

    const tdLng = document.createElement('td');
    tdLng.style.padding = '4px 6px';
    const inpLng = makeNumberInput(lng, 'Lng');
    tdLng.appendChild(inpLng);

    const tdDel = document.createElement('td');
    tdDel.style.padding = '4px 6px';
    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.innerHTML = MAP_EDITOR_ICONS['delete'];
    delBtn.title = this.translate.instant('Map_delete_vertex_tr');
    delBtn.setAttribute('aria-label', delBtn.title);
    delBtn.style.cssText = EDITOR_BUTTON_STYLE + 'width:28px;height:28px;padding:4px;color:#b42318;';
    delBtn.querySelector('svg')!.style.cssText = 'display:block;width:18px;height:18px;';
    delBtn.addEventListener('click', () => {
      tr.remove();
      rebuildRowIndices();
    });
    tdDel.appendChild(delBtn);

    tr.appendChild(tdIdx);
    tr.appendChild(tdLat);
    tr.appendChild(tdLng);
    tr.appendChild(tdDel);
    tbody.appendChild(tr);

    if (renumber) rebuildRowIndices();
  };

  if (openRing.length === 0) {
    addRow(0, 0);
    addRow(0, 0);
    addRow(0, 0);
  } else {
    for (const [lng, lat] of openRing) addRow(lat, lng, false);
    rebuildRowIndices();
  }

  addBtn.addEventListener('click', async () => {
    const nextCount = tbody.rows.length + 1;
    if (nextCount > 100 && !largePolygonConfirmed) {
      addBtn.disabled = true;
      try {
        if (!(await this.confirmLargePolygon(nextCount))) return;
        if (!this.draw.get(active.id) || !wrap.isConnected) return;
        largePolygonConfirmed = true;
      } finally {
        addBtn.disabled = false;
      }
    }
    addRow();
  });
  cancelBtn.addEventListener('click', () => {
    try {
      this.coordsPopup?.remove();
    } finally {
      this.coordsPopup = null;
    }
  });

  saveBtn.addEventListener('click', () => {
    const rows = Array.from(tbody.querySelectorAll('tr'));
    const newOpen: [number, number][] = [];
    for (const tr of rows) {
      const inputs = tr.querySelectorAll('input');
      const lat = parseFloat((inputs[0] as HTMLInputElement).value);
      const lng = parseFloat((inputs[1] as HTMLInputElement).value);
      if (!Number.isFinite(lat) || !Number.isFinite(lng))
        return this.showErr('All_rows_must_have_valid_numeric_Latitude_and_Longitude_tr');
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180)
        return this.showErr('Lat_must_be_between_90_and_Lng_between_180_tr');
      newOpen.push([lng, lat]);
    }
    if (newOpen.length < 3) return this.showErr('A_polygon_needs_at_least_3_vertices_tr');
    const holes = (active.feature.geometry.coordinates?.slice(1) ?? []) as [number, number][][];
    const holeOutside = firstHoleOutsideRing(newOpen, holes);
    if (holeOutside >= 0) return this.showErrWith('Map_hole_outside_area_tr', { index: holeOutside + 1 });

    const name = uniqueFeatureName(nameInput.value, this.draw.getAll().features, active.id);
    const nextId = this.replacePolygon(active.id, newOpen, { name });
    if (nextId) {
      this.showMsg('Coordinates_updated_tr');
      this.refreshCtrl();
      this.coordsPopup?.remove();
      this.coordsPopup = null;
    }
  });

  try {
    this.coordsPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 16, maxWidth: '460px' })
      .setLngLat(at)
      .setDOMContent(wrap)
      .addTo(this.map);
  } catch (err) {
    this.showErr('Could_not_open_coordinates_editor_tr', err);
  }
}
import { uniqueFeatureName } from './map-feature-names';
